// The ROUTINE DETAIL panel (Scheduler Screen v2, the "Signal" re-layout) — one routine opened by clicking
// its list row or its day-agenda entry. It LEADS with what the routine does, then the occurrences the
// scheduler module has PROJECTED for it (G-110), then its recent Run Records with the
// failed/interrupted-vs-completed distinction the module RECORDED (G-107/G-111), and it carries EVERY
// per-routine action — Run now, the Pause/Resume toggle, Restore (archived only), Remove.
//
// THE PANEL IS NOW THE ONLY PLACE THE ACTIONS LIVE. The owner's complaint (11 Aug 2026) was that every list
// row carried its own action cluster and outcome chips, and that opening a routine still never said what it
// was FOR. So the row went calm and this panel took the controls — the same RoutineActions components, so
// the honest-affordance rules (Run now on a live routine only; the toggle's word from the recorded state;
// Remove's two clicks) are unchanged rather than re-implemented. Remove had to be ADDED here: it only ever
// existed on the row, so pulling it off the row without adding it here would have made removal unreachable.
//
// WHAT THIS PANEL SAYS THE ROUTINE DOES — and where the words come from. The brief is the execution brief
// the Schedule was CREATED with; expected output is a narrative note, while the typed output contract is
// the machine-checked definition of success. All are projected through to the browser without inference.
// Nothing here paraphrases, summarises or infers: a routine whose brief is somehow empty says so in words
// rather than showing a blank paragraph or a plausible sentence the record does not contain.
//
// WHAT THE DESIGN SHOWS AND THIS PANEL STILL DOES NOT, and why. The design's detail rail also carries a
// cadence phrase, a "kept by", first-run history and a numbered list of steps, plus an Edit button. None of
// that is on the wire: rendering a cadence sentence would mean the client inventing one from occurrence
// spacing, a "kept by" would mean guessing an owner, and steps would be fabricated outright. So those stay
// OMITTED and the layout closes up (P-SELF, G-182). Edit is omitted for a different reason: the owner
// wants edits made THROUGH RITONA (ruling 2 Sep 2026 — "Ritona can edit routines that are live"), so the
// panel shows the revision number and who last edited it rather than a form of its own.
//
// Tokens only (G-181); the panel renders the routine's NAME, never its id (G-197).

import type { ScheduleHealthScheduleRow } from '../projections.ts';
import { Icon } from '../../app/Icon.tsx';
import { StatusPill, type Tone } from '../primitives.tsx';
import {
  actorLabel,
  editedByLine,
  fmtClock,
  runMarker,
  scheduleProjectionPresentation,
} from './scheduler-format.ts';
import { scheduleMachineSuccessText } from './schedule-output-presentation.ts';
import {
  RunNowAction,
  LifecycleToggleAction,
  RestoreAction,
  RemoveAction,
  type RoutineRoutes,
} from './RoutineActions.tsx';

/** The lifecycle pill vocabulary — Live is the user-facing word for the recorded `active` state. */
const STATE_PILL: Readonly<
  Record<ScheduleHealthScheduleRow['state'], { readonly tone: Tone; readonly label: string }>
> = {
  active: { tone: 'go', label: 'Live' },
  paused: { tone: 'attention', label: 'Paused' },
  draft: { tone: 'neutral', label: 'Draft' },
  finished: { tone: 'neutral', label: 'Finished' },
  archived: { tone: 'neutral', label: 'Archived' },
};

/** REVISION + EDITED-BY (owner ruling 2 Sep 2026: Ritona edits live routines on request, so the board must
 *  say which revision this is and who last changed it) and, for an archived routine, the recorded reason.
 *  Every word comes from the projection; the panel names no person by id (G-197). */
function Provenance({ row }: { readonly row: ScheduleHealthScheduleRow }): React.JSX.Element {
  const archive = row.archiveRecord;
  return (
    <section className="rd-sec" aria-label="Revision">
      <p className="rd-done">
        <span className="k">Revision</span>
        <span className="v">{editedByLine(row)}</span>
      </p>
      {row.state === 'archived' && archive !== null ? (
        <p className="rd-done">
          <span className="k">Archived</span>
          <span className="v">
            {`${archive.reason} — by ${
              archive.archivedByActor === 'agent' && archive.requestedByUserId !== null
                ? 'Ritona, as asked by a person'
                : actorLabel(archive.archivedByActor)
            } at ${fmtClock(archive.archivedAt)} on ${archive.archivedAt.slice(0, 10)}`}
          </span>
        </p>
      ) : null}
    </section>
  );
}

/** The runs note beside the pill — the module's own landed tally over its recent window, or nothing yet. */
function runsNote(row: ScheduleHealthScheduleRow): string {
  const landed = row.outcomeTally.completed;
  if (landed === 0 && row.recentRuns.length === 0) return 'no runs yet';
  return `${String(landed)} landed`;
}

/** The verbatim placeholder for a routine carrying no recorded brief. The schema makes an empty brief
 *  unreachable today, but the render path does not TRUST that: a blank paragraph would read as "this
 *  routine does nothing", and an invented sentence would be worse. */
const NO_BRIEF = 'No brief recorded for this routine.';

/** WHAT THIS ROUTINE DOES — the panel's first section, and the whole point of opening a routine. Both lines
 *  are the RECORDED text rendered as written (G-182); the panel authors no description of its own. */
function WhatItDoes({ row }: { readonly row: ScheduleHealthScheduleRow }): React.JSX.Element {
  const brief = row.brief.trim();
  const outputNote = row.expectedOutput.trim();
  return (
    <section className="rd-sec" aria-label="What this routine does">
      <div className="mono-label">What this routine does</div>
      <p className={brief.length === 0 ? 'rd-empty' : 'rd-brief'}>
        {brief.length === 0 ? NO_BRIEF : brief}
      </p>
      <p className="rd-done">
        <span className="k">Machine success</span>
        <span className="v">{scheduleMachineSuccessText(row.outputContract)}</span>
      </p>
      {/* Human prose is supporting context only. It is never labelled or evaluated as "done when". */}
      {outputNote.length > 0 ? (
        <p className="rd-done">
          <span className="k">Narrative output note</span>
          <span className="v">{outputNote}</span>
        </p>
      ) : null}
    </section>
  );
}

/** Projected dates remain visible for draft/paused definitions but are explicitly non-executing. */
function NextRuns({ row }: { readonly row: ScheduleHealthScheduleRow }): React.JSX.Element {
  const presentation = scheduleProjectionPresentation(row.state);
  return (
    <section className="rd-sec" aria-label={presentation.sectionLabel}>
      <div className="mono-label">{presentation.sectionLabel}</div>
      {presentation.nonExecutingNote !== null ? (
        <p className="rd-empty">{presentation.nonExecutingNote}</p>
      ) : null}
      {row.nextOccurrences.length === 0 ? (
        row.state === 'finished' || row.state === 'archived' ? null : (
          <p className="rd-empty">Nothing is projected.</p>
        )
      ) : (
        <ul className="rd-list">
          {row.nextOccurrences.map((occurrence) => (
            <li key={occurrence.instant} className="rd-kv">
              <span className="k">{occurrence.weekday}</span>
              <span className="v">
                {occurrence.localDate} · {fmtClock(occurrence.instant)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The recent Run Records, newest first as the module ordered them — failed/interrupted stay visibly
 *  distinct from completed, because that is the distinction the module RECORDED (G-107/G-111, G-182). */
function RecentRuns({ row }: { readonly row: ScheduleHealthScheduleRow }): React.JSX.Element {
  return (
    <section className="rd-sec" aria-label="Recent runs">
      <div className="mono-label">Recent runs</div>
      {row.recentRuns.length === 0 ? (
        <p className="rd-empty">No runs recorded yet.</p>
      ) : (
        <ul className="rd-list">
          {row.recentRuns.map((run) => {
            const attention = run.state === 'failed' || run.state === 'interrupted';
            return (
              <li key={run.id} className="rd-run" data-run-state={run.state}>
                <span className={attention ? 'dot pending' : 'dot'} aria-hidden="true" />
                <span className="wh">{fmtClock(run.startedAt)}</span>
                <span className="nt">{runMarker(run)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** What the detail panel renders and routes. */
export interface RoutineDetailProps {
  readonly row: ScheduleHealthScheduleRow;
  readonly routes: RoutineRoutes;
  readonly onClose: () => void;
}

/** The routine detail panel — a read of the projection plus the two lifecycle actions. */
export function RoutineDetail({ row, routes, onClose }: RoutineDetailProps): React.JSX.Element {
  const pill = STATE_PILL[row.state];
  // The explicit role is load-bearing: an `aside` nested inside sectioning content does not map to
  // `complementary` in every accessibility mapping, and this panel must stay reachable as a landmark.
  return (
    <aside
      className="rd-panel"
      role="complementary"
      aria-label={`Routine detail: ${row.name}`}
      data-health={row.health}
    >
      <div className="rd-head">
        <div className="rd-title">
          <span className="nm">{row.name}</span>
          <span className="rd-meta">
            <StatusPill tone={pill.tone}>{pill.label}</StatusPill>
            <span className="mono-label">{runsNote(row)}</span>
          </span>
        </div>
        <button
          type="button"
          className="rd-close"
          aria-label="Close routine detail"
          onClick={onClose}
        >
          <Icon name="x" size={15} />
        </button>
      </div>
      <div className="rd-body">
        <WhatItDoes row={row} />
        <Provenance row={row} />
        <NextRuns row={row} />
        <RecentRuns row={row} />
      </div>
      {/* Every per-routine control, and the ONLY place any of them renders now. */}
      <div className="rd-foot">
        {row.state === 'active' ? <RunNowAction row={row} onRunNow={routes.onRunNow} /> : null}
        <LifecycleToggleAction row={row} onPause={routes.onPause} onResume={routes.onResume} />
        <RestoreAction row={row} onRestore={routes.onRestore} />
        <RemoveAction row={row} onRemove={routes.onRemove} />
      </div>
    </aside>
  );
}
